import { clarity } from 'clarity-js';
import { ReactNode, useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService } from 'services';
import { AuthContext } from 'contexts';
import { AuthData, Brand, Store, StoresByBrand, UserInfo } from 'types';
import { getLocalStorageItem, messages, setLocalStorageItem } from 'helpers';
import {
  BRAND_PROPERTIES,
  INVOICE_OPTIONS,
  LABEL_SIZE,
  LocalStorageKeys,
} from 'commons';

interface ComponentProps {
  children: ReactNode;
}

const ADMIN_ROLES = [
  'ROLE_STORESALEADMINGROUP',
  'ROLE_STORESALEOPERATIONGROUP',
  'ROLE_STORESALEFINANCIALGROUP',
] as const;

export function AuthProvider({ children }: ComponentProps) {
  const storedUser = getLocalStorageItem(LocalStorageKeys.User, undefined);
  const storedStores = storedUser?.stores || [];
  const storedSelectedStore = getLocalStorageItem(
    LocalStorageKeys.SelectedStore,
    {}
  );

  const [user, setUser] = useState<any>(storedUser);
  const [stores, setStores] = useState<StoresByBrand[]>(storedStores);
  const [selectedStore, setSelectedStore] = useState<any>(storedSelectedStore);
  const [signed, setSigned] = useState<boolean>(Boolean(storedUser));

  const navigate = useNavigate();

  const previousAppVersion = getLocalStorageItem(LocalStorageKeys.AppVersion);
  const currentAppVersion = process.env.REACT_APP_VERSION;
  if (previousAppVersion !== currentAppVersion) {
    signOut();

    setLocalStorageItem(LocalStorageKeys.AppVersion, currentAppVersion);
  }

  useEffect(() => {
    if (!selectedStore.name) return;

    const { name, brand } = selectedStore;
    // TODO: After implementing Redux this can be removed
    clarity.identify(`${brand} - ${name}`);
  }, [selectedStore]);

  function getInitialStore(data: UserInfo): Store {
    const [initialStore] = data.stores;
    const { brand, access, stores } = initialStore;

    return {
      brand: brand.toLowerCase() as Brand,
      name: stores[0],
      access,
    };
  }

  function formatUserInfo(data: UserInfo[]): UserInfo {
    const [item1, item2] = data;

    function getIsAdmin(roles: string[]): boolean {
      return roles.some(role => ADMIN_ROLES.includes(role as any));
    }

    if (!item2) {
      const isAdmin = getIsAdmin(item1.role);

      return { ...item1, isAdmin };
    }

    const filteredAndSortedBrands = [...item1.stores, ...item2.stores]
      .filter(
        (store, index, self) =>
          index === self.findIndex((s) => s.brand === store.brand)
      )
      .sort((a, b) =>
        BRAND_PROPERTIES[a.brand]?.displayName.localeCompare(
          BRAND_PROPERTIES[b.brand]?.displayName
        )
      );

    const brandsSortedStores = filteredAndSortedBrands.map((brand) => {
      const sortedStores = brand.stores.sort((a, b) =>
        a.toLowerCase().localeCompare(b.toLowerCase())
      );

      brand.stores = sortedStores;

      return brand;
    });

    const role = [...new Set([...item1.role, ...item2.role])];
    const isAdmin = getIsAdmin(role);

    return {
      name: item1.name,
      email: item1.email,
      isAdmin,
      stores: brandsSortedStores,
      role,
    };
  }

  const signIn = useCallback(
    async (form: { email: string; password: string }, redirectPath = '/') => {
      const { email, password } = form;
      const { data, error } = await authService.authentication({
        email,
        password,
      });

      if (error) {
        messages([{ errorCode: 'FRONT-RE-LOGIN_FAILED' }]);
        setSigned(false);
      }

      if (data) {
        const response = await Promise.all(
          data.map(async (auth: AuthData) => {
            const { brand, success } = auth;
            const { access_token, refresh_token } = success;

            const { data } = await authService.getWhoAmI({
              access_token,
              brand,
            });

            const { email, groups, name } = data;
            const { brandGroups, roleGroups, storeGroups } = groups;

            const storesWithBrands = storeGroups.map((stores: string) => {
              const [store, brand] = stores.split('|');

              return {
                brand: brand.toLowerCase(),
                store,
              };
            });

            const stores = brandGroups.map((brand: string) => {
              const storeBrand: { brand: string; store: string }[] =
                storesWithBrands.filter((stores: { brand: string }) => {
                  return stores.brand.toLowerCase() === brand.toLowerCase();
                });

              return {
                brand: brand.toLowerCase(),
                stores: storeBrand.map((brand) => brand.store),
                access: `${access_token}|${refresh_token}`,
              };
            });

            return {
              name,
              email,
              stores,
              role: roleGroups,
            };
          })
        );

        const userInfo = formatUserInfo(response);
        const initialStore = getInitialStore(userInfo);

        setLocalStorageItem(LocalStorageKeys.User, userInfo);
        setLocalStorageItem(LocalStorageKeys.SelectedStore, initialStore);

        setUser(userInfo);
        setStores(userInfo.stores);
        setSelectedStore(initialStore);
        setSigned(true);

        navigate(redirectPath);

        return messages([
          { errorCode: 'FRONT-RE-LOGIN_SUCCESS' },
          { subject: userInfo?.name, subjectType: 'employeeName' },
        ]);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  function cleanLocalStorageAndSaveUserOptions() {
    const labelSize = getLocalStorageItem(
      LocalStorageKeys.LabelSize,
      LABEL_SIZE.small
    );
    const notificationSound = getLocalStorageItem(
      LocalStorageKeys.SoundNotification,
      true
    );
    const currentAppVersion = getLocalStorageItem(
      LocalStorageKeys.AppVersion,
      process.env.REACT_APP_VERSION
    );
    const invoiceOption = getLocalStorageItem(
      LocalStorageKeys.InvoiceOption,
      INVOICE_OPTIONS.invoiceKey
    );

    const employeeList = getLocalStorageItem(LocalStorageKeys.EmployeeData, []);

    localStorage.clear();

    setLocalStorageItem(LocalStorageKeys.LabelSize, labelSize);
    setLocalStorageItem(LocalStorageKeys.SoundNotification, notificationSound);
    setLocalStorageItem(LocalStorageKeys.AppVersion, currentAppVersion);
    setLocalStorageItem(LocalStorageKeys.InvoiceOption, invoiceOption);
    setLocalStorageItem(LocalStorageKeys.EmployeeData, employeeList);
  }

  function signOut() {
    cleanLocalStorageAndSaveUserOptions();

    setSigned(false);
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        setUser,
        stores,
        selectedStore,
        setSelectedStore,
        signIn,
        signOut,
        signed,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
